import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { DivisionAnalysisMap, type DivisionMarker } from "@/components/DivisionAnalysisMap";
import { DivisionAnalysisDisplay } from "@/components/DivisionAnalysisDisplay";
import { MaintainerManagement } from "@/components/MaintainerManagement";
import { DivisionBriefingBetaBanner } from "@/components/DivisionBriefing/DivisionBriefingBetaBanner";
import { DivisionBriefingDisplay } from "@/components/DivisionBriefing/DivisionBriefingDisplay";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Edit, Plus, Settings } from "lucide-react";
import type { DivisionAnalysis } from "@/lib/db/division-analysis";
import type { UnitMap } from "@/lib/types/deck-builder";
import { useDataStore } from "@/lib/store/data-store";
import { useAuth } from "@/lib/store/auth-store";
import { analyzeDivision, computeDivisionBaseline } from "@/lib/analysis/division-analysis-engine";
import { groupDivisionsForSelect } from "@/lib/utils/division-select-groups";
import { DivisionGroupedPicker } from "@/components/DivisionGroupedPicker";
import { cn } from "@/lib/utils";

const TAB_COMMUNITY = "community";
const TAB_BRIEFING = "briefing";

function parseTab(searchParams: URLSearchParams): typeof TAB_COMMUNITY | typeof TAB_BRIEFING {
  return searchParams.get("tab") === TAB_BRIEFING ? TAB_BRIEFING : TAB_COMMUNITY;
}

export function DivisionsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseTab(searchParams);

  const descriptor = searchParams.get("division") || "";
  const selectedDescriptor = descriptor || null;

  const [analyses, setAnalyses] = useState<DivisionAnalysis[]>([]);
  const [analysesLoading, setAnalysesLoading] = useState(true);
  const { units, weapons, ammunition, divisions, loadingUnits, loadingWeapons, loadingDivisions } =
    useDataStore();
  const { user } = useAuth();
  const [unitMap, setUnitMap] = useState<UnitMap>({});
  const isAdmin = user?.isAdmin || false;
  const canEditAnalysis = isAdmin || user?.isMaintainer || false;
  const [showMaintainerManagement, setShowMaintainerManagement] = useState(false);

  useEffect(() => {
    async function loadAnalyses() {
      try {
        setAnalysesLoading(true);
        const response = await fetch("/api/division-analysis");
        if (response.ok) {
          const data = await response.json();
          setAnalyses(data);
        }
      } catch (error) {
        console.error("Failed to load division analyses:", error);
      } finally {
        setAnalysesLoading(false);
      }
    }
    void loadAnalyses();
  }, []);

  useEffect(() => {
    if (units) {
      const map: UnitMap = {};
      units.forEach((unit) => {
        map[unit.descriptorName] = unit;
      });
      setUnitMap(map);
    }
  }, [units]);

  const selectedAnalysis =
    analyses.find((a) => a.divisionDescriptor === selectedDescriptor) || null;
  const selectedDivision = divisions?.find((d) => d.descriptor === selectedDescriptor);

  const markers: DivisionMarker[] = analyses
    .filter((a) => a.mapX !== null && a.mapY !== null)
    .map((a) => ({
      divisionDescriptor: a.divisionDescriptor,
      x: a.mapX!,
      y: a.mapY!,
      lateArrival: false,
    }));

  const divisionGroups = useMemo(
    () => (divisions ? groupDivisionsForSelect(divisions) : []),
    [divisions]
  );

  const briefingDataReady = Boolean(units && weapons && divisions && ammunition);
  const briefingLoading = loadingUnits || loadingWeapons || loadingDivisions || !briefingDataReady;

  const allProfiles = useMemo(() => {
    if (!divisions || !units || !weapons || !ammunition) return [];
    return divisions.map((d) => analyzeDivision(d, units, weapons, ammunition));
  }, [divisions, units, weapons, ammunition]);

  const baseline = useMemo(
    () => computeDivisionBaseline(allProfiles, weapons),
    [allProfiles, weapons]
  );

  const profile = useMemo(
    () => allProfiles.find((p) => p.divisionDescriptor === descriptor),
    [allProfiles, descriptor]
  );

  const setDivision = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set("division", value);
    } else {
      next.delete("division");
    }
    setSearchParams(next, { replace: true });
  };

  const setTab = (tab: typeof TAB_COMMUNITY | typeof TAB_BRIEFING) => {
    const next = new URLSearchParams(searchParams);
    if (tab === TAB_BRIEFING) {
      next.set("tab", TAB_BRIEFING);
    } else {
      next.delete("tab");
    }
    setSearchParams(next, { replace: true });
  };

  if (analysesLoading) {
    return (
      <div className="flex min-h-[50vh] flex-1 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between px-4 py-2">
        <h1 className="text-3xl font-bold">Divisions</h1>
        <div className="flex gap-2">
          {isAdmin && (
            <Button
              variant={showMaintainerManagement ? "default" : "outline"}
              onClick={() => setShowMaintainerManagement(!showMaintainerManagement)}
            >
              <Settings className="mr-2 h-4 w-4" />
              {showMaintainerManagement ? "Hide Maintainers" : "Manage Maintainers"}
            </Button>
          )}
          {canEditAnalysis && (
            <Button variant="outline" onClick={() => navigate("/divisions/editor")}>
              <Plus className="mr-2 h-4 w-4" />
              New
            </Button>
          )}
          {canEditAnalysis && (
            <Button
              variant="outline"
              onClick={() => {
                if (selectedDescriptor) {
                  navigate(`/divisions/editor/${encodeURIComponent(selectedDescriptor)}`);
                } else {
                  navigate("/divisions/editor");
                }
              }}
            >
              <Edit className="mr-2 h-4 w-4" />
              {selectedDescriptor ? "Edit" : "New"}
            </Button>
          )}
        </div>
      </div>

      <div className="flex shrink-0 gap-2 border-b px-4">
        <button
          type="button"
          onClick={() => setTab(TAB_COMMUNITY)}
          className={cn(
            "-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors",
            activeTab === TAB_COMMUNITY
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          Community
        </button>
        <button
          type="button"
          onClick={() => setTab(TAB_BRIEFING)}
          className={cn(
            "-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors",
            activeTab === TAB_BRIEFING
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          Briefing
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 font-semibold">
            Beta
          </Badge>
        </button>
      </div>

      {isAdmin && showMaintainerManagement && (
        <div className="flex-1 overflow-auto px-4 py-2">
          <MaintainerManagement onClose={() => setShowMaintainerManagement(false)} />
        </div>
      )}

      {!showMaintainerManagement && (
        <>
          <div className="shrink-0 border-b bg-background/95 px-4 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex flex-wrap items-center gap-3">
              <DivisionGroupedPicker
                divisionGroups={divisionGroups}
                value={descriptor}
                onValueChange={setDivision}
                selectedDivision={selectedDivision}
                divisionHref={(d) => {
                  const next = new URLSearchParams(searchParams);
                  next.set("division", d);
                  return `/divisions?${next.toString()}`;
                }}
              />
              {activeTab === TAB_BRIEFING && profile && (
                <div className="flex shrink-0 items-center gap-3 text-sm text-muted-foreground">
                  <Badge variant="secondary" className="text-sm">
                    {profile.overview.playstyleTag}
                  </Badge>
                  <span>
                    <span className="font-medium text-foreground">
                      {profile.overview.maxActivationPoints}
                    </span>{" "}
                    AP ·{" "}
                    <span className="font-medium text-foreground">
                      {profile.overview.totalPackCount}
                    </span>{" "}
                    packs
                  </span>
                </div>
              )}
            </div>
          </div>

          {activeTab === TAB_COMMUNITY && (
            <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-hidden px-4 py-2 lg:grid-cols-[40%_60%]">
              <div className="hidden min-h-0 flex-col gap-3 lg:flex">
                <div className="min-h-0 flex-1">
                  <DivisionAnalysisMap
                    markers={markers}
                    selectedDivisionDescriptor={selectedDescriptor || undefined}
                    onDivisionClick={(d) => setDivision(d)}
                    showCoordinates={true}
                  />
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-hidden">
                <DivisionAnalysisDisplay
                  analysis={selectedAnalysis}
                  division={selectedDivision}
                  unitMap={unitMap}
                />
              </div>
            </div>
          )}

          {activeTab === TAB_BRIEFING && (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              <DivisionBriefingBetaBanner />
              <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain p-3">
                {briefingLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : !profile ? (
                  <div className="flex h-64 items-center justify-center text-muted-foreground">
                    <p>Select a division to view its briefing</p>
                  </div>
                ) : (
                  <DivisionBriefingDisplay profile={profile} baseline={baseline} />
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
